import React from 'react';
import { Users, Heart, Download, Flag } from 'lucide-react';
import type { EngagementSection } from '../../api/insights';
import shared from './shared.module.css';
import styles from './GuestEngagementGroup.module.css';

interface Props {
  data: EngagementSection;
}

function DownloadTrend({ days }: { days: { date: string; count: number }[] }) {
  const max = Math.max(...days.map((d) => d.count), 1);
  const width = 100;
  const height = 60;
  const step = width / (days.length - 1 || 1);
  const points = days.map((d, i) => {
    const x = i * step;
    const y = height - (d.count / max) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={styles['trend-svg']} aria-label="Download trend last 30 days" preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke="var(--clay-orange)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={`0,${height} ${points} ${width},${height}`} fill="rgba(239,68,68,0.1)" stroke="none" />
    </svg>
  );
}

export function GuestEngagementGroup({ data }: Props) {
  const maxMemberCount = data.topAlbumsByMembers[0]?.memberCount ?? 1;
  const maxDownloadCount = data.downloads.topAlbums[0]?.count ?? 1;
  const hasReports = data.reports.pending > 0;

  return (
    <div className={shared['group-card']}>
      <div className={shared['group-header']}>
        <div className={shared['group-icon']}><Users size={15} /></div>
        <div>
          <div className={shared['group-title']}>
            Guest Engagement &amp; Activity
            {hasReports && (
              <span className={`${shared.badge} ${shared['badge--red']} ${styles['reports-badge']}`}>
                {data.reports.pending} report{data.reports.pending > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className={shared['group-subtitle']}>Are guests using the platform and taking photos home?</div>
        </div>
      </div>

      {/* Core stats */}
      <div className={shared['stat-row']}>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}><Users size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />Members</span>
          <span className={shared['stat-value']}>{data.totalMembers.toLocaleString()}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}><Heart size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />Likes</span>
          <span className={shared['stat-value']}>{data.totalLikes.toLocaleString()}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}><Download size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />Downloads</span>
          <span className={shared['stat-value']}>{data.downloads.total.toLocaleString()}</span>
        </div>
      </div>

      {/* Download type split */}
      {data.downloads.total > 0 && (
        <div className={styles['type-split']}>
          {[
            { label: 'Single photo', value: data.downloads.byType.single },
            { label: 'Batch', value: data.downloads.byType.batch },
            { label: 'Full ZIP', value: data.downloads.byType.all },
          ].map(({ label, value }) => (
            <div key={label} className={styles['type-chip']}>
              <span className={styles['type-chip-value']}>{data.downloads.total > 0 ? Math.round((value / data.downloads.total) * 100) : 0}%</span>
              <span className={styles['type-chip-label']}>{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Download trend */}
      <div>
        <div className={shared['section-label']}>Downloads — last 30 days</div>
        <DownloadTrend days={data.downloads.trendLast30Days} />
      </div>

      {/* Top liked photos */}
      {data.topLikedPhotos.length > 0 && (
        <div>
          <div className={shared['section-label']}>Most liked photos</div>
          <div className={styles['liked-row']}>
            {data.topLikedPhotos.map((p) => (
              <div key={p.photoId} className={styles['liked-card']}>
                {p.thumbnailUrl ? (
                  <img src={p.thumbnailUrl} alt="" className={styles['liked-thumb']} />
                ) : (
                  <div className={styles['liked-thumb-placeholder']} />
                )}
                <div className={styles['liked-meta']}>
                  <Heart size={10} />
                  <span>{p.likeCount}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top albums by downloads */}
      {data.downloads.topAlbums.length > 0 && (
        <div>
          <div className={shared['section-label']}>Most downloaded albums</div>
          <div className={shared['ranked-list']}>
            {data.downloads.topAlbums.map((a, i) => (
              <div key={a.albumId} className={shared['ranked-item']}>
                <div className={shared['rank-badge']}>{i + 1}</div>
                <span className={shared['ranked-label']}>{a.albumName}</span>
                <div className={shared['ranked-bar']} style={{ width: `${Math.round((a.count / maxDownloadCount) * 80)}px` }} />
                <span className={shared['ranked-count']}>{a.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top albums by members */}
      {data.topAlbumsByMembers.length > 0 && (
        <div>
          <div className={shared['section-label']}>Most joined albums</div>
          <div className={shared['ranked-list']}>
            {data.topAlbumsByMembers.map((a, i) => (
              <div key={a.albumId} className={shared['ranked-item']}>
                <div className={shared['rank-badge']}>{i + 1}</div>
                <span className={shared['ranked-label']}>{a.albumName}</span>
                <div className={shared['ranked-bar']} style={{ width: `${Math.round((a.memberCount / maxMemberCount) * 80)}px` }} />
                <span className={shared['ranked-count']}>{a.memberCount}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reports */}
      {hasReports && (
        <div className={styles['reports-section']}>
          <div className={styles['reports-header']}>
            <Flag size={13} className={styles['reports-icon']} />
            <span className={styles['reports-title']}>{data.reports.pending} pending report{data.reports.pending > 1 ? 's' : ''} need review</span>
          </div>
          <div className={styles['reports-reasons']}>
            {Object.entries(data.reports.byReason).map(([reason, count]) => (
              <span key={reason} className={`${shared.badge} ${shared['badge--neutral']}`}>{reason}: {count}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
